'use client';

import type {
  GameAnswerResult,
  GameSessionResult
} from '@shared/all/domains/games';
import { QUESTIONS_PER_ROUND, TIMER_SECONDS } from '@shared/all/domains/games';
import toast from '@shared/utils/helpers/toast';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useReducer, useRef } from 'react';

import { client } from '../../../utils/trpc';
import styles from '../game.module.css';

type Question = GameSessionResult['questions'][number];
type AnswerResult = GameAnswerResult;

const TIMER_MS = TIMER_SECONDS * 1000;
const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const LOW_TIME_SECONDS = 5;

/**
 * One reducer instead of a pile of useState — the phases are mutually
 * exclusive, so a state machine keeps impossible combinations (e.g. showing a
 * reveal while the timer still runs) unrepresentable.
 */
type State = {
  phase: 'loading' | 'question' | 'reveal';
  sessionId: number | null;
  questions: Question[];
  index: number;
  /**
   * Wall-clock deadline for the current question; drives the countdown.
   * 0 means the clock has not been armed yet — see the ARM action.
   */
  deadline: number;
  secondsLeft: number;
  result: AnswerResult | null;
  submitting: boolean;
  answeredBefore: number;
};

type Action =
  | { type: 'STARTED'; session: GameSessionResult }
  | { type: 'ARM'; now: number }
  | { type: 'TICK'; now: number }
  | { type: 'SUBMITTING' }
  | { type: 'REVEALED'; result: AnswerResult }
  | { type: 'SUBMIT_FAILED' }
  | { type: 'NEXT' }
  | { type: 'FINISHING' };

const initialState: State = {
  phase: 'loading',
  sessionId: null,
  questions: [],
  index: 0,
  deadline: 0,
  secondsLeft: TIMER_SECONDS,
  result: null,
  submitting: false,
  answeredBefore: 0
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'STARTED':
      return {
        ...state,
        phase: 'question',
        sessionId: action.session.sessionId,
        questions: action.session.questions,
        answeredBefore: action.session.answeredCount,
        deadline: 0,
        secondsLeft: TIMER_SECONDS
      };
    // The clock starts only once the question is actually on screen and the
    // app has stopped loading — otherwise a slow load silently burns seconds
    // of a timed round the player never got to see.
    case 'ARM':
      return { ...state, deadline: action.now + TIMER_MS };
    case 'TICK':
      return {
        ...state,
        secondsLeft: Math.max(
          0,
          Math.ceil((state.deadline - action.now) / 1000)
        )
      };
    case 'SUBMITTING':
      return { ...state, submitting: true };
    case 'REVEALED':
      return {
        ...state,
        phase: 'reveal',
        submitting: false,
        result: action.result
      };
    case 'SUBMIT_FAILED':
      return { ...state, submitting: false };
    case 'NEXT':
      return {
        ...state,
        phase: 'question',
        index: state.index + 1,
        result: null,
        deadline: 0,
        secondsLeft: TIMER_SECONDS
      };
    // Stays in the reveal phase while endSession settles, so the last card
    // remains on screen instead of flashing empty before the redirect.
    case 'FINISHING':
      return { ...state, submitting: true };
    default:
      return state;
  }
};

export default function GamePlay() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isDaily = searchParams?.get('mode') === 'daily';

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    phase,
    sessionId,
    questions,
    index,
    secondsLeft,
    result,
    submitting,
    answeredBefore
  } = state;

  const cardRef = useRef<HTMLDivElement>(null);

  const question = questions[index];
  const totalThisRound = answeredBefore + questions.length;
  const questionNumber = answeredBefore + index + 1;
  const isLastQuestion = index + 1 >= questions.length;

  // Start (or resume) the session on mount.
  useEffect(() => {
    let ignore = false;

    const start = async () => {
      const [error, data] = await client.games.startSession.mutate({ isDaily });
      if (ignore) return;

      if (error) {
        toast(error.message, '', 'error');
        router.push('/fact');
        return;
      }

      if (data.questions.length === 0) {
        toast('No questions left in this round', '', 'info');
        router.push('/fact');
        return;
      }

      dispatch({ type: 'STARTED', session: data });
    };

    start();

    return () => {
      ignore = true;
    };
  }, [isDaily, router]);

  const submitAnswer = async (answeredFact: boolean, timedOut = false) => {
    if (!sessionId || !question || submitting) return;

    dispatch({ type: 'SUBMITTING' });

    const elapsed = TIMER_MS - Math.max(0, state.deadline - Date.now());
    const timeMs = timedOut
      ? TIMER_MS
      : Math.min(Math.round(elapsed), TIMER_MS);

    const [error, data] = await client.games.submitAnswer.mutate({
      sessionId,
      postId: question.postId,
      answeredFact,
      timeMs
    });

    if (error) {
      toast(error.message, '', 'error');
      dispatch({ type: 'SUBMIT_FAILED' });
      return;
    }

    dispatch({ type: 'REVEALED', result: data });
  };

  // Arm the clock only once the card is genuinely reachable by a click.
  //
  // The app renders a full-screen splash over every route until its global
  // queries settle, and it swallows pointer events. A countdown started on data
  // arrival therefore burns real seconds — and can auto-submit a question as
  // wrong — while the player is still looking at the splash. Hit-testing the
  // card is overlay-agnostic: it holds for the splash, a modal, or anything
  // else that lands on top.
  useEffect(() => {
    if (phase !== 'question' || state.deadline !== 0) return;

    const card = cardRef.current;
    if (!card) return;

    const isReachable = () => {
      const box = card.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return false;
      const hit = document.elementFromPoint(
        box.left + box.width / 2,
        box.top + box.height / 2
      );
      return !!hit && card.contains(hit);
    };

    if (isReachable()) {
      dispatch({ type: 'ARM', now: Date.now() });
      return;
    }

    const poll = setInterval(() => {
      if (isReachable()) dispatch({ type: 'ARM', now: Date.now() });
    }, 100);

    return () => clearInterval(poll);
  }, [phase, state.deadline, index]);

  // Countdown: one interval for the displayed seconds, one timeout for the
  // expiry itself. The ring animation is CSS, so neither drives the visuals.
  useEffect(() => {
    if (phase !== 'question' || submitting || state.deadline === 0) return;

    const tick = setInterval(
      () => dispatch({ type: 'TICK', now: Date.now() }),
      250
    );
    const expiry = setTimeout(
      () => submitAnswer(false, true),
      Math.max(0, state.deadline - Date.now())
    );

    return () => {
      clearInterval(tick);
      clearTimeout(expiry);
    };
    // Re-armed per question; `deadline` changes on every NEXT.
  }, [phase, submitting, state.deadline]);

  const goNext = async () => {
    if (!isLastQuestion) {
      dispatch({ type: 'NEXT' });
      return;
    }

    if (!sessionId) return;
    dispatch({ type: 'FINISHING' });

    const [error] = await client.games.endSession.mutate({ sessionId });
    if (error) toast(error.message, '', 'error');

    router.push(`/fact/results?sessionId=${sessionId}`);
  };

  if (phase === 'loading' || !question) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-ds-border-standard border-t-primary" />
        <p className="text-sm text-textSecondary">Dealing your questions…</p>
      </div>
    );
  }

  const armed = state.deadline !== 0;
  const lowTime = armed && secondsLeft <= LOW_TIME_SECONDS;
  const showingReveal = phase === 'reveal' && !!result;

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col justify-center px-4 py-6 sm:py-10">
      {/* Header: mode, progress, streak */}
      <header className="mb-5 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-emphasis uppercase tracking-wide text-textSecondary">
            {isDaily ? 'Daily Challenge' : 'Free Play'}
          </p>
          <p className="text-sm text-textPrimary">
            Question {questionNumber} of {totalThisRound || QUESTIONS_PER_ROUND}
          </p>
        </div>

        {result && result.streak > 1 && (
          <span
            key={result.streak}
            className={`${styles.streakBadge} rounded-full bg-orange-500/15 px-3 py-1 text-sm font-emphasis text-orange-500`}
          >
            🔥 {result.streak} streak
          </span>
        )}
      </header>

      {/* Progress dots */}
      <div className="mb-6 flex items-center gap-1.5" aria-hidden="true">
        {Array.from({ length: totalThisRound }, (_, dot) => {
          // While a reveal is up, the question being revealed is already
          // answered — it should read as filled, not as pending.
          const position = answeredBefore + index;
          const answered =
            dot < position || (dot === position && showingReveal);
          const current = dot === position;
          return (
            <span
              key={dot}
              className={`${styles.dot} ${current ? styles.dotActive : ''} h-1.5 flex-1 rounded-full ${
                answered
                  ? 'bg-primary'
                  : current
                    ? 'bg-primary/60'
                    : 'bg-ds-border-standard'
              }`}
            />
          );
        })}
      </div>

      {/* Question card + timer */}
      <div>
        <div
          key={question.postId}
          ref={cardRef}
          className={`${styles.card} rounded-2xl border border-ds-border-standard bg-background-secondary p-6 shadow-sm`}
        >
          <div className="mb-5 flex items-start gap-4">
            {/* Countdown ring — CSS-animated, restarted by the key change */}
            <div className="relative h-16 w-16 shrink-0">
              <svg viewBox="0 0 60 60" className="h-16 w-16">
                <circle
                  className={styles.ringTrack}
                  cx="30"
                  cy="30"
                  r={RING_RADIUS}
                  fill="none"
                  strokeWidth="4"
                />
                {/* Only rendered once the clock is armed, so the ring never
                 * drains ahead of the countdown it represents. */}
                {!showingReveal && armed && (
                  <circle
                    key={question.postId}
                    className={styles.ringProgress}
                    cx="30"
                    cy="30"
                    r={RING_RADIUS}
                    fill="none"
                    strokeWidth="4"
                    strokeLinecap="round"
                    stroke={lowTime ? 'rgb(239 35 60)' : 'rgb(var(--primary))'}
                    strokeDasharray={RING_CIRCUMFERENCE}
                    style={
                      {
                        '--ring-circumference': RING_CIRCUMFERENCE,
                        '--timer-duration': `${TIMER_SECONDS}s`,
                        animationPlayState: submitting ? 'paused' : 'running'
                      } as React.CSSProperties
                    }
                  />
                )}
              </svg>
              <span
                className={`absolute inset-0 flex items-center justify-center text-lg font-emphasis tabular-nums ${
                  lowTime && !showingReveal
                    ? 'text-red-500'
                    : 'text-textPrimary'
                }`}
              >
                {showingReveal ? '✓' : secondsLeft}
              </span>
            </div>

            <p className="pt-1 text-xs uppercase tracking-wide text-textSecondary">
              Fact or fiction?
            </p>
          </div>

          <p className="text-balance text-xl leading-relaxed text-textPrimary">
            {question.content}
          </p>
        </div>

        {/* Reveal */}
        {showingReveal && (
          <div className={`${styles.reveal} mt-4 space-y-3`}>
            <div
              className={`${result.correct ? styles.correctPop : styles.wrongShake} rounded-2xl border p-4 ${
                result.correct
                  ? 'border-green-500/30 bg-green-500/10'
                  : 'border-red-500/30 bg-red-500/10'
              }`}
            >
              <p
                className={`text-lg font-emphasis ${
                  result.correct ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {result.correct ? 'Correct!' : 'Not quite'}
              </p>
              <p className="mt-0.5 text-sm text-textPrimary">
                This statement is{' '}
                <strong>{result.isFact ? 'a Fact' : 'Fiction'}</strong>.
              </p>
            </div>

            {/* The question bank is community-written, so whoever posted the
             * claim is credited here once the answer is locked in. */}
            {result.source?.authorUsername && (
              <p className="text-sm text-textSecondary">
                Question by{' '}
                <Link
                  href={`/user/${result.source.authorUsername}`}
                  className="font-emphasis text-textPrimary hover:underline"
                >
                  @{result.source.authorUsername}
                </Link>
              </p>
            )}

            {(result.source?.citation || result.source?.categories.length) && (
              <div className="rounded-2xl border border-ds-border-standard bg-background-light p-4">
                {result.source.categories.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {result.source.categories.map((category) => (
                      <span
                        key={category.slug}
                        className="rounded-full bg-background-secondary px-2.5 py-0.5 text-xs text-textSecondary"
                      >
                        {category.name}
                      </span>
                    ))}
                  </div>
                )}
                {result.source.citation && (
                  <a
                    href={result.source.citation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-sm text-primary hover:underline"
                  >
                    Source: {result.source.citation}
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6">
        {showingReveal ? (
          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className={`${styles.answerButton} w-full rounded-full bg-primary px-6 py-4 text-base font-emphasis text-white disabled:opacity-60`}
          >
            {submitting
              ? 'Scoring…'
              : isLastQuestion
                ? 'See results'
                : 'Next question'}
          </button>
        ) : (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => submitAnswer(true)}
              disabled={submitting}
              className={`${styles.answerButton} flex-1 rounded-full bg-green-600 px-6 py-4 text-base font-emphasis text-white disabled:opacity-50`}
            >
              Fact
            </button>
            <button
              type="button"
              onClick={() => submitAnswer(false)}
              disabled={submitting}
              className={`${styles.answerButton} flex-1 rounded-full bg-destructive px-6 py-4 text-base font-emphasis text-white disabled:opacity-50`}
            >
              Fiction
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
